import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Hero from "@/components/site/Hero";
import EditorialMarquee from "@/components/site/EditorialMarquee";
import VehicleCard from "@/components/site/VehicleCard";
import ManifestoChapters from "@/components/site/ManifestoChapters";
import MaklanDivisions from "@/components/site/MaklanDivisions";
import Testimonials from "@/components/site/Testimonials";
import Pillars from "@/components/site/Pillars";
import MaklanStandard from "@/components/site/MaklanStandard";
import GlobalNetwork from "@/components/site/GlobalNetwork";
import HowItWorks from "@/components/site/HowItWorks";
import CantFindItCTA from "@/components/site/CantFindItCTA";
import PartnerWithUs from "@/components/site/PartnerWithUs";
import { RevealOnScroll } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  const featured = vehicles.filter((v) => v.featured).slice(0, 3);

  return (
    <div data-testid="home-page">
      <Hero />

      <Pillars />

      <EditorialMarquee />

      {/* Featured Collection */}
      <section data-testid="featured-inventory" className="section-y-lg bg-ink border-t border-white/5">
        <div className="container-editorial">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end mb-20 md:mb-24">
            <div className="col-span-12 md:col-span-8">
              <motion.span
                initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
                className="block h-px bg-[#C5A059] w-14 mb-8 origin-left"
              />
              <div className="overline text-[#C5A059] mb-6">Motors · Recent Sourcings</div>
              <RevealOnScroll
                element="h2"
                className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
                lines={["Sourced with intent.", "Not from a <em>lot</em>."]}
              />
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <Link to="/inventory" data-testid="home-view-all-inventory" className="btn-editorial">
                View All <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {featured.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
          </div>
        </div>
      </section>

      <ManifestoChapters />

      <HowItWorks />

      <MaklanStandard />

      <GlobalNetwork />

      {/* Concierge Services */}
      <section data-testid="service-ctas" className="section-y bg-navy border-t border-white/5">
        <div className="container-editorial">
          <div className="grid grid-cols-12 gap-8 md:gap-16 mb-20">
            <div className="col-span-12 md:col-span-7">
              <motion.span
                initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
                className="block h-px bg-[#C5A059] w-14 mb-8 origin-left"
              />
              <div className="overline text-[#C5A059] mb-6">Concierge Services</div>
              <RevealOnScroll
                element="h2"
                className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
                lines={["Three quiet doors", "into <em>Maklan Motors</em>."]}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-14 gap-y-12">
            {[
              { title: "Fleet & Corporate", desc: "Considered pricing and delivery for orders of five vehicles or more.", to: "/fleet", n: "I" },
              { title: "Import on Request", desc: "Name the vehicle. We source, ship and deliver — quietly.", to: "/import", n: "II" },
              { title: "Concierge Enquiries", desc: "Written correspondence for private clients. By appointment.", to: "/contact", n: "III" },
            ].map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.2, delay: i * 0.1, ease: [0.19, 1, 0.22, 1] }}
                className="pb-10 border-b border-white/10"
              >
                <div className="text-[#C5A059] font-display italic text-4xl leading-none">{c.n}</div>
                <h3 className="font-display text-3xl mt-6 leading-tight">{c.title}</h3>
                <div className="w-10 h-px bg-white/15 my-5" />
                <p className="editorial-body max-w-sm">{c.desc}</p>
                <Link to={c.to} data-testid={`service-cta-${c.title.toLowerCase().replace(/\s+/g,'-').replace(/[&]/g,'and')}`} className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#C5A059] link-under">
                  Enquire <ArrowRight size={12} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <PartnerWithUs />

      <CantFindItCTA />

      <MaklanDivisions />
    </div>
  );
}
